import React, { useState, useEffect } from 'react';
import { channelsAPI, usersAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Loader2, Hash, Lock, Check } from 'lucide-react';
import { toast } from 'sonner';

export function ChannelCreateModal({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setIsPrivate(true);
      setSelectedUserIds([]);
      setLoadingUsers(true);
      usersAPI.list().then(res => {
        setTeamUsers((res.data || []).filter(u => u.id !== user.id && u.is_active));
      }).catch(() => {}).finally(() => setLoadingUsers(false));
    }
  }, [open, user?.id]);

  const toggleUser = (uid) => {
    setSelectedUserIds(prev =>
      prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
    );
  };

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Channel name is required'); return; }
    setCreating(true);
    try {
      const res = await channelsAPI.create({
        name: name.trim(),
        description: description.trim() || null,
        is_private: isPrivate,
        member_user_ids: selectedUserIds,
      });
      toast.success(`#${res.data.name} created`);
      onCreated?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create channel');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="create-channel-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Create Channel</DialogTitle>
          <DialogDescription>Channels organize team conversations by topic</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label htmlFor="ch-name">Channel Name</Label>
            <div className="relative mt-1">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="ch-name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. project-updates"
                className="pl-9"
                maxLength={100}
                data-testid="channel-name-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="ch-desc">Description (optional)</Label>
            <Input
              id="ch-desc"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What is this channel about?"
              className="mt-1"
              maxLength={500}
              data-testid="channel-description-input"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-muted-foreground" />
              <Label htmlFor="ch-private" className="cursor-pointer">Private Channel</Label>
            </div>
            <Switch
              id="ch-private"
              checked={isPrivate}
              onCheckedChange={setIsPrivate}
              data-testid="channel-private-toggle"
            />
          </div>
          {isPrivate && (
            <p className="text-xs text-muted-foreground -mt-2 ml-6">Only invited members can see and post</p>
          )}

          <div>
            <Label>Add Members</Label>
            <div className="mt-1 max-h-40 overflow-y-auto border rounded-md">
              {loadingUsers ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              ) : teamUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-3">No other team members</p>
              ) : teamUsers.map(u => (
                <div
                  key={u.id}
                  className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors hover:bg-muted/50 ${
                    selectedUserIds.includes(u.id) ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => toggleUser(u.id)}
                  data-testid={`channel-member-${u.id}`}
                >
                  <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                    selectedUserIds.includes(u.id)
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'border-input'
                  }`}>
                    {selectedUserIds.includes(u.id) && <Check className="w-3 h-3" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{u.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{u.role}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">You are automatically added as admin</p>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating || !name.trim()} data-testid="create-channel-submit">
            {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : 'Create Channel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
